import {
  ADD_DELIVERABLE_TYPE,
  AddDeliverableType,
} from "../../../../../../../../../helpers/constants";
import { UnitTypesKey } from "../../../../../../../../../helpers/constants/siteCoreKeys";
import { KeyValueMap } from "../../../../../../../../../redux/slices/appState/models";
import {
  ProjectCreateActivityDefaultFormKeys,
  ProjectCreateActivityGeneralFormKeys,
  ProjectCreateDeliverablesFormKeys,
  ProjectUpdateDeliverablesFormKeys,
} from "./models";

import * as yup from "yup";
export const UpdateProjectDeliverableValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape({
    [ProjectUpdateDeliverablesFormKeys.measurementMinute]: yup
      .string()
      .when([ProjectUpdateDeliverablesFormKeys.units], {
        is: (unitValue: string) => {
          return unitValue === UnitTypesKey.Minutes;
        },
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
      })
      .default(""),
    [ProjectUpdateDeliverablesFormKeys.measurementOther]: yup
      .number()
      .nullable()
      .when([ProjectUpdateDeliverablesFormKeys.units], {
        is: (unitValue: string) => {
          return (
            unitValue === UnitTypesKey.Count || unitValue === UnitTypesKey.Days
          );
        },
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .typeError(commonValidationMessages.requiredValidation)
            .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
            .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
      })
      .default(0),
  });

export const ProjectAddDeliverableValidationSchema = (
  commonValidationMessages: KeyValueMap,
  userLang: string,
) =>
  yup.object().shape({
    [ProjectCreateDeliverablesFormKeys.addDeliverableType]: yup
      .mixed<AddDeliverableType>()
      .oneOf([
        ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType,
        ADD_DELIVERABLE_TYPE.existingKey as AddDeliverableType,
      ])
      .required()
      .default(ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType),

    [ProjectCreateDeliverablesFormKeys.deliverableType]: yup
      .string()
      .when([ProjectCreateDeliverablesFormKeys.addDeliverableType], {
        is: (addDeliverableType: AddDeliverableType) =>
          addDeliverableType === ADD_DELIVERABLE_TYPE.newKey,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      }),

    [`${ProjectCreateDeliverablesFormKeys.name}.${userLang}`]: yup
      .string()
      .test(
        "required-when-new",
        commonValidationMessages.requiredValidation,
        function () {
          const { parent } = this;
          const deliverablesValue = parent[
            ProjectCreateDeliverablesFormKeys.name
          ][userLang] as string;
          const addDeliverableType =
            parent[ProjectCreateDeliverablesFormKeys.addDeliverableType];
          if (addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
            return !!deliverablesValue?.trim();
          }
          return true;
        },
      ),

    [ProjectCreateDeliverablesFormKeys.units]: yup
      .string()
      .required(commonValidationMessages.requiredValidation)
      .when([ProjectCreateDeliverablesFormKeys.addDeliverableType], {
        is: (addDeliverableType: AddDeliverableType) =>
          addDeliverableType === ADD_DELIVERABLE_TYPE.newKey,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      }),

    [ProjectCreateDeliverablesFormKeys.measurementMinute]: yup
      .string()
      .nullable()
      .when(
        [
          ProjectCreateDeliverablesFormKeys.units,
          ProjectCreateDeliverablesFormKeys.addDeliverableType,
        ],
        {
          is: (unitValue: string, addDeliverableType: AddDeliverableType) =>
            addDeliverableType === ADD_DELIVERABLE_TYPE.newKey &&
            unitValue === UnitTypesKey.Minutes,
          then: (schema) =>
            schema
              .required(commonValidationMessages.requiredValidation)
              .default(""),
          otherwise: (schema) => schema.notRequired(),
        },
      ),

    [ProjectCreateDeliverablesFormKeys.measurementOther]: yup
      .number()
      .nullable()
      .when(
        [
          ProjectCreateDeliverablesFormKeys.units,
          ProjectCreateDeliverablesFormKeys.addDeliverableType,
        ],
        {
          is: (unitValue: string, addDeliverableType: AddDeliverableType) =>
            addDeliverableType === ADD_DELIVERABLE_TYPE.newKey &&
            (unitValue === UnitTypesKey.Count ||
              unitValue === UnitTypesKey.Days),
          then: (schema) =>
            schema
              .required(commonValidationMessages.requiredValidation)
              .typeError(commonValidationMessages.requiredValidation)
              .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
              .max(99, commonValidationMessages.valueRangeFieldValidator1To99)
              .default(0),
          otherwise: (schema) => schema.notRequired(),
        },
      ),
  });

export const ProjectActivityGeneralValidationSchema = (
  commonValidationMessages: KeyValueMap,
  userLang: string,
) =>
  yup.object().shape({
    [ProjectCreateActivityGeneralFormKeys.activityType]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
    [ProjectCreateActivityGeneralFormKeys.modality]: yup
      .array()
      .of(yup.string().required())
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [ProjectCreateActivityGeneralFormKeys.topic]: yup.object().shape({
      [userLang]: yup
        .string()
        .required(commonValidationMessages.requiredValidation),
    }),
    [ProjectCreateActivityGeneralFormKeys.deliverables]: yup
      .array()
      .default([]),
    [ProjectCreateActivityGeneralFormKeys.activityStatus]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
    [ProjectCreateActivityGeneralFormKeys.triggerBilling]: yup
      .boolean()
      .default(false),
    [ProjectCreateActivityGeneralFormKeys.lastContactDate]: yup
      .boolean()
      .default(false),
    [ProjectCreateActivityGeneralFormKeys.triggerTouch]: yup
      .boolean()
      .default(false),
  });

export const CreateActivityDefaultValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape({
    [ProjectCreateActivityDefaultFormKeys.units]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
    [ProjectCreateActivityDefaultFormKeys.measurementMinute]: yup
      .string()
      .when([ProjectCreateActivityDefaultFormKeys.units], {
        is: (unitValue: string) => {
          return unitValue === UnitTypesKey.Minutes;
        },
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
      })
      .default(""),
    [ProjectCreateActivityDefaultFormKeys.measurementOther]: yup
      .number()
      .nullable()
      .when([ProjectCreateActivityDefaultFormKeys.units], {
        is: (unitValue: string) => {
          return (
            unitValue === UnitTypesKey.Count || unitValue === UnitTypesKey.Days
          );
        },
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .typeError(commonValidationMessages.requiredValidation)
            .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
            .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
      })
      .default(0),
  });
